import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sun, Moon } from "lucide-react";
import { AVAILABLE_LANGS } from "../../locales/languages";

export const AdminSettings = () => {
  const { t, i18n } = useTranslation();
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );

  const toggleTheme = () => {
    setDarkMode((prev) => !prev);

    if (darkMode) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangDropdownOpen(false);
  };

  const selectedLanguage =
    AVAILABLE_LANGS.find((lang) => lang.id === i18n.language)?.name ??
    i18n.language;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isLangDropdownOpen &&
        langDropdownRef.current &&
        !langDropdownRef.current.contains(e.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isLangDropdownOpen]);

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-8">{t("Settings")}</h1>

      <div className="bg-popupLight dark:bg-popupDark rounded-lg p-5 shadow mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-semi text-lg mb-1">{t("Theme")}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("Switch between light and dark mode")}
          </p>
        </div>

        <button
          onClick={toggleTheme}
          className="p-3 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition"
        >
          {darkMode ? (
            <Sun className="w-5 h-5" />
          ) : (
            <Moon className="w-5 h-5" />
          )}
        </button>
      </div>

      <div className="bg-popupLight dark:bg-popupDark rounded-lg p-5 shadow flex items-center justify-between">
        <div>
          <h2 className="font-semi text-lg mb-1">{t("Language")}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("Select your preferred language")}
          </p>
        </div>

        <div className="relative" ref={langDropdownRef}>
          <button
            onClick={() => setIsLangDropdownOpen((prev) => !prev)}
            className="px-4 py-2 rounded-lg bg-primaryBg dark:bg-primaryBgDark hover:opacity-90 transition text-sm font-medium"
          >
            {selectedLanguage}
          </button>

          {isLangDropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 max-h-48 overflow-y-auto bg-primaryBg dark:bg-primaryBgDark shadow-lg rounded-md p-2 z-50 custom-scroll">
              {AVAILABLE_LANGS.map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => handleLanguageChange(lang.id)}
                  className={`w-full text-left px-3 py-1 text-sm rounded flex justify-between items-center ${
                    i18n.language === lang.id
                      ? "bg-gray-200 dark:bg-gray-700 font-medium"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {lang.name}
                  {i18n.language === lang.id && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
